import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarCheck, Heart, ShoppingBag, Clock, CheckCircle2, PackageCheck, XCircle, Loader2, ArrowRight, MailCheck, Phone, CalendarPlus } from "lucide-react";
import { GlassCard, StatusBadge } from "../../components/common/ui";
import { Button } from "../../components/ui/button";
import { formatGBP } from "../../components/common/StockBadge";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

const STATUS_LABEL = {
  RESERVATION_RECEIVED: "Reservation Received", UNDER_REVIEW: "Under Review", CONFIRMED: "Confirmed",
  READY_FOR_COLLECTION: "Ready for Collection", COLLECTED: "Collected", CANCELLED: "Cancelled",
};

function NextCollectionBanner({ nc }) {
  const download = async () => {
    try {
      const res = await api.get(`/me/reservations/${nc.reservation_number}/calendar.ics`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/calendar" }));
      const a = document.createElement("a"); a.href = url; a.download = `${nc.reservation_number}.ics`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Calendar file downloaded");
    } catch { toast.error("Could not generate calendar file."); }
  };
  return (
    <div data-testid="next-collection-banner" className="rounded-2xl border border-gold/30 bg-gradient-to-r from-gold/10 to-transparent p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
      <div className="min-w-0">
        <p className="text-xs tracking-[0.25em] uppercase text-gold font-bold">Next collection</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-white font-semibold" data-testid="next-collection-number">{nc.reservation_number}</span>
          <StatusBadge status={STATUS_LABEL[nc.status] || "Received"} />
        </div>
        <p className="mt-1 text-sm text-white/70">Collect on <strong className="text-white">{nc.collection_date}</strong> · Estimated <strong className="text-white">{formatGBP(nc.estimated_total)}</strong> payable in store</p>
      </div>
      <div className="flex gap-2 shrink-0">
        <Link to={`/reservation/${nc.reservation_number}`}><Button data-testid="next-collection-view-btn" className="cf-gold-btn font-semibold gap-2">View <ArrowRight className="h-4 w-4" /></Button></Link>
        <Button data-testid="next-collection-calendar-btn" onClick={download} variant="outline" className="border-gold/40 text-gold hover:bg-gold/10 gap-2"><CalendarPlus className="h-4 w-4" /> Add to Calendar</Button>
      </div>
    </div>
  );
}

const STATUS_CARDS = [
  ["RESERVATION_RECEIVED", "Awaiting Review", Clock],
  ["CONFIRMED", "Confirmed", CheckCircle2],
  ["READY_FOR_COLLECTION", "Ready for Collection", PackageCheck],
  ["COLLECTED", "Collected", CheckCircle2],
  ["CANCELLED", "Cancelled", XCircle],
];

export default function CustomerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ov, setOv] = useState(null);

  useEffect(() => { api.get("/me/overview").then(({ data }) => setOv(data)).catch(() => setOv(false)); }, []);

  const by = ov?.reservations_by_status || {};

  return (
    <div className="space-y-8" data-testid="customer-dashboard">
      <div>
        <p className="text-xs tracking-[0.3em] uppercase cf-gold-text font-bold">My Account</p>
        <h1 className="mt-2 font-playfair text-3xl text-white">Welcome back, {user?.name?.split(" ")[0]}</h1>
        <p className="mt-2 text-muted-foreground">Track your Click & Collect reservations and manage your account.</p>
      </div>

      {ov === null ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-gold" /></div>
      ) : (
        <>
          {ov?.next_collection && <NextCollectionBanner nc={ov.next_collection} />}
          {/* Top summary */}
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <GlassCard className="p-6 cursor-pointer" data-testid="stat-reservations" onClick={() => navigate("/dashboard/reservations")}>
              <div className="flex items-center justify-between">
                <div className="grid place-items-center h-11 w-11 rounded-xl bg-gold/10 border border-gold/20"><CalendarCheck className="h-5 w-5 text-gold" /></div>
                <span className="font-playfair text-2xl text-white" data-testid="stat-reservations-value">{ov?.reservations_total ?? 0}</span>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">Total Reservations</p>
            </GlassCard>
            <GlassCard className="p-6 cursor-pointer" data-testid="stat-favourites" onClick={() => navigate("/dashboard/favourites")}>
              <div className="flex items-center justify-between">
                <div className="grid place-items-center h-11 w-11 rounded-xl bg-gold/10 border border-gold/20"><Heart className="h-5 w-5 text-gold" /></div>
                <span className="font-playfair text-2xl text-white" data-testid="stat-favourites-value">{ov?.favourites_count ?? 0}</span>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">Saved Favourites</p>
            </GlassCard>
            <GlassCard className="p-6 cursor-pointer" data-testid="stat-basket" onClick={() => navigate("/basket")}>
              <div className="flex items-center justify-between">
                <div className="grid place-items-center h-11 w-11 rounded-xl bg-gold/10 border border-gold/20"><ShoppingBag className="h-5 w-5 text-gold" /></div>
                <span className="font-playfair text-2xl text-white" data-testid="stat-basket-value">{ov?.basket_count ?? 0}</span>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">In Your Basket</p>
            </GlassCard>
            <GlassCard className="p-6" data-testid="stat-newsletter">
              <div className="flex items-center justify-between">
                <div className="grid place-items-center h-11 w-11 rounded-xl bg-gold/10 border border-gold/20"><MailCheck className="h-5 w-5 text-gold" /></div>
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${ov?.profile?.marketing_consent ? "bg-emerald-500/15 text-emerald-400" : "bg-white/10 text-white/50"}`}>{ov?.profile?.marketing_consent ? "Subscribed" : "Not subscribed"}</span>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">VIP Newsletter</p>
            </GlassCard>
          </div>

          {/* Reservation status breakdown */}
          <div>
            <h2 className="font-playfair text-xl text-white mb-4">Reservation status</h2>
            <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
              {STATUS_CARDS.map(([key, label, Icon]) => (
                <GlassCard key={key} className="p-5" hover={false} data-testid={`status-${key}`}>
                  <Icon className="h-5 w-5 text-gold" />
                  <p className="mt-3 font-playfair text-2xl text-white">{by[key] ?? 0}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{label}</p>
                </GlassCard>
              ))}
            </div>
          </div>

          {/* Account snapshot */}
          <GlassCard className="p-6" hover={false} data-testid="account-snapshot">
            <h2 className="font-playfair text-xl text-white">Your details</h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-y-3 gap-x-8 text-sm">
              <div className="flex items-center gap-2 text-white/80"><MailCheck className="h-4 w-4 text-gold" /> {ov?.profile?.email}
                {ov?.profile?.is_verified && <span className="text-emerald-400 text-xs">· Verified</span>}
              </div>
              <div className="flex items-center gap-2 text-white/80"><Phone className="h-4 w-4 text-gold" /> {ov?.profile?.phone || "No mobile added"}</div>
            </div>
            <Button variant="ghost" className="mt-4 px-0 text-gold hover:text-amber gap-1 h-auto" data-testid="edit-profile-link" onClick={() => navigate("/dashboard/profile")}>Manage account <ArrowRight className="h-4 w-4" /></Button>
          </GlassCard>

          <Button className="cf-gold-btn rounded-full font-semibold" onClick={() => navigate("/fireworks")} data-testid="browse-range-btn">Browse The Range</Button>
        </>
      )}
    </div>
  );
}
